import { isNumericValueType, formatReportValue } from "@/features/reports/lib/field-formatters"
import type { NormalizedReport } from "@/features/reports/model/normalized-report"

/**
 * A normalized report, as the PDF renderer wants it.
 *
 * Built from the SAME columns, formatter and labels the on-screen table uses — the sibling of
 * `reportToCsv`, deliberately so. A second formatting path would be a second set of rounding decisions,
 * and the first time they diverged a treasurer would be holding a PDF that disagreed with the screen it
 * came from.
 *
 * Values leave here as display TEXT. The server re-formats nothing; it only draws.
 */

export type ReportPdfColumn = { key: string; label: string; alignment?: "left" | "center" | "right" }

export type ReportPdfPayload = {
  title: string
  columns: ReportPdfColumn[]
  rows: Array<Record<string, string>>
  filters?: Array<{ label: string; value: string }>
  summary?: Array<{ label: string; value: string }>
}

export function buildReportPdfPayload(input: {
  report: NormalizedReport
  title: string
  resolveLabel: (labelKey: string) => string
  currency: string
  filters?: Array<{ label: string; value: string }>
}): ReportPdfPayload {
  const columns = input.report.columns ?? []
  const rows = input.report.rows ?? []

  return {
    title: input.title,
    // Numbers right-align so a column of money reads as a column; everything else stays left.
    columns: columns.map((column) => ({
      key: column.key,
      label: input.resolveLabel(column.labelKey),
      alignment: isNumericValueType(column.valueType) ? ("right" as const) : ("left" as const),
    })),
    rows: rows.map((row) => {
      const out: Record<string, string> = {}
      for (const column of columns) {
        out[column.key] = formatReportValue(row[column.key], column.valueType, input.currency)
      }
      return out
    }),
    filters: input.filters,
    /*
     * The report's own summary cards become the strip, rather than letting the renderer total the columns
     * itself. A report already decided which figures are its headline — and several of them are ratios or
     * counts that a column sum would get wrong.
     */
    summary: (input.report.summaryCards ?? [])
      .map((card) => ({
        label: input.resolveLabel(card.labelKey),
        value: formatReportValue(card.value, card.valueType, input.currency),
      }))
      .slice(0, 4),
  }
}
